import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import Helmet from 'react-helmet';

import FallbackAvatar from './FallbackAvatar';
import { ActionButton } from './Button';

const MORE_REVIEWS_HEIGHT = 800;

const avatarStyles = `
  border-radius: 100px;
  float: left;
  height: 60px;
  margin: -12px 20px 15px;
  width: 60px;
`;

const Wrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
`;

const ColumnsWrapper = styled.div`
  max-height: ${p => p.columnHeight || '100%'};
  transition: all 0.3s ease;
`;

const Columns = styled.div`
  align-items: start;
  display: grid;
  grid-gap: 12px;
  grid-template-columns: 1fr 1fr;
  margin: 0 auto;
  max-width: 1200px;
  padding: 0 20px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialWrapper = styled.div`
  background: ${p => (p.$altBg ? 'var(--nude-200)' : '#fff')};
  box-shadow: ${p => (p.$altBg ? 'none' : '0 2px 18px rgba(0,0,0,.025)')};
  display: flex;
  flex-direction: column;
  margin: 20px;
  padding: 20px 30px;
  position: relative;
  text-align: left;
  text-decoration: none;
  width: 100%;
  z-index: 2;

  a {
    cursor: pointer;
    display: block;
    font-size: 14px;
    text-align: center;
  }

  @media (max-width: 700px) {
    margin: 10px 0;
    padding: 20px 15px;
    width: 100%;
    flex-basis: 100%;
  }

  &:after {
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    color: #444;
    font-size: 18px;
    opacity: 0;
    justify-content: center;
    align-items: center;
    font-weight: bold;
    position: absolute;
    top: 0;
    left: 0;

    @media (max-width: 700px) {
      opacity: 1;
      position: static;
      text-shadow: none;
      margin-top: 10px;
      order: 20;
      font-size: 14px;
      color: currentColor;
    }
  }
`;

const Author = styled.div`
  color: #333;
  display: block;
  font-family: 'tertiarytypeface', Georgia, 'Times New Roman', Times, serif;
  font-size: 19px;
  font-style: normal;
  font-weight: 700;
  padding-top: 30px;
  margin: -10px 0 10px -10px;
  overflow: hidden;

  @media (max-width: 700px) {
    display: flex;
    flex-direction: column;
    text-align: center;
    align-items: center;
    font-size: 15px;
    order: 10;
  }
`;

const Avatar = styled.img`
  ${avatarStyles}
`;

const Quote = styled.div`
  border: none;
  color: #777;
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
  padding: 0;
  white-space: normal;

  @media (max-width: 700px) {
    font-size: 15px;
  }
`;

const Stars = styled.div`
  color: var(--nude-500);
  font-size: 20px;
  letter-spacing: 2px;
  margin: 20px auto;
  text-align: center;
`;

const FallbackAvatarWrapper = styled.div`
  ${avatarStyles}
`;

const FadeOutEffect = styled.div`
  background-image: ${p =>
    p.altBg
      ? 'linear-gradient(to bottom, transparent, #fff)'
      : 'linear-gradient(to bottom, transparent, var(--nude-200))'};
  z-index: 2;
  width: 100%;
  height: 70px;
`;

const ReadMoreButton = styled(ActionButton)`
  margin: 40px 0 0;
`;

const Testimonial = ({
  bg = false,
  img,
  href,
  name,
  location,
  quote,
  rating = 5,
  platform = 'Trustpilot',
  aggregateRating = 5,
  totalReviews = 10,
}) => {
  return (
    <TestimonialWrapper $altBg={bg}>
      <div>
        <Author>
          {img && <Avatar src={img} alt={`Avatar of ${name}`} />}
          {!img && (
            <FallbackAvatarWrapper>
              <FallbackAvatar />
            </FallbackAvatarWrapper>
          )}
          {name} {location && <span>— {location}</span>}
        </Author>
        <Quote>{quote}</Quote>
        <Stars>
          {_.times(rating, i => (
            <span key={i}>★</span>
          ))}
        </Stars>
      </div>
      <Helmet>
        <script type="application/ld+json">{`
            {
              "@context": "http://schema.org",
              "@type": "WebApplication",
              "applicationCategory": "BrowserApplication",
              "operatingSystem": "WEB",
              "countriesSupported": "au,gb,uk,nz,ca,us",
              "name": "Say I do",
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "${aggregateRating}",
                "ratingCount": "${totalReviews}"
              },
              "offers": {
                "@type": "Offer",
                "price": "50.00",
                "priceCurrency": "USD"
              },
              "review": {
                "@type": "Review",
                "author": {
                  "@type": "Person",
                  "name":"${name}"
                },
                "description": "Wedding Website and Planning Tool",
                "reviewBody": "${quote}",
                "reviewRating": {
                  "@type": "Rating",
                  "bestRating": "5",
                  "ratingValue": "${rating}",
                  "worstRating": "1"
                }
              }
            }
          `}</script>
      </Helmet>
    </TestimonialWrapper>
  );
};

export default function Testimonials({ bg = false, data = false }) {
  const totalReviews = data?.length || 0;
  const aggregateRating =
    totalReviews > 0 ? data.reduce((e, o) => e + o.rating, 0) / totalReviews : 0;

  const randomisedTestimonials = React.useMemo(() => {
    const initialReviews = data
      ?.sort(() => Math.random() - 0.5)
      ?.sort((a, b) => (!!a.image ? (!!b.image ? 0 : -1) : 1));

    return initialReviews;
  }, [data]);

  const [columnHeight, setColumnHeight] = useState(700);
  const [isOverflowing, setIsOverflowing] = useState(true);
  const [remainingChunkHeight, setRemainingChunkHeight] = useState(0);

  useEffect(() => {
    const columnsWrapper = document.querySelector('.columns-wrapper');
    if (columnsWrapper) {
      setIsOverflowing(columnsWrapper.scrollHeight > columnsWrapper.clientHeight);
      setRemainingChunkHeight(
        columnsWrapper.scrollHeight - columnsWrapper.clientHeight
      );
    }
  }, [columnHeight, randomisedTestimonials]);

  const handleReadMore = useCallback(() => {
    setColumnHeight(prevHeight => prevHeight + MORE_REVIEWS_HEIGHT);
  }, []);

  const isMoreToShow = remainingChunkHeight > MORE_REVIEWS_HEIGHT && isOverflowing;

  return (
    <>
      <Wrapper>
        <ColumnsWrapper
          className="columns-wrapper"
          columnHeight={`${columnHeight}px`}
        >
          <Columns>
            {randomisedTestimonials?.map((testimonial, index) => (
              <Testimonial
                bg={bg}
                key={index}
                platform={testimonial.platform}
                rating={testimonial.rating}
                href={testimonial.href}
                img={testimonial.image?.url || testimonial.image}
                name={testimonial.name}
                location={testimonial.location}
                quote={testimonial.quote}
                aggregateRating={aggregateRating}
                totalReviews={totalReviews}
              />
            ))}
          </Columns>
        </ColumnsWrapper>
        {isOverflowing && <FadeOutEffect altBg={bg} />}

        <Helmet>
          <script type="application/ld+json">{`
            {
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "Say I do",
              "applicationCategory": "BrowserApplication",
              "countriesSupported": "au,gb,nz,ca,us,de,za,id",
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "${aggregateRating}",
                "ratingCount": "${totalReviews}"
              },
              "offers": {
                "@type": "Offer",
                "price": "30.00",
                "priceCurrency": "USD"
              }
            }
          `}</script>
        </Helmet>
      </Wrapper>
      {isMoreToShow && (
        <ReadMoreButton onClick={handleReadMore}>Show more reviews</ReadMoreButton>
      )}
    </>
  );
}
